import { useRef, useState } from 'react';
import { MarkerF, InfoBoxF } from '@react-google-maps/api';
import { useLocation, useNavigate } from 'react-router-dom';
import AlertSvgRed from './alert-circle-red.svg';
import AlertSvgOrange from './alert-circle-orange.svg';
import AlertSvgYellow from './alert-circle-yellow.svg';
import { alertEventsMap } from '../../../../pages/Alerts/constants/alertEventsMap';
import { buildAlertDetailsLocationState } from '../../../../utils/alertNavigation';
import S from './AlertMarkerStyles';

type Props = {
  id: string;
  lat: number;
  lng: number;
  name: string;
  riskLevel: string;
  disableNavigation?: boolean;
  onClick?: () => void;
};

const getSvgByRisk = (riskLevel: string): string => ({
  low: AlertSvgYellow,
  medium: AlertSvgOrange,
  urgent: AlertSvgRed,
})[riskLevel] || AlertSvgRed;

function AlertMarker({
  lat, lng, riskLevel, name, id, disableNavigation, onClick,
}: Props): JSX.Element {
  const [showInfoBox, setShowInfoBox] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isNavigatingRef = useRef(false);

  const handleMarkerClick = (e: google.maps.MapMouseEvent): void => {
    e.domEvent?.stopPropagation();
    e.domEvent?.preventDefault?.();

    const targetPath = `/alerts/${id}`;
    if (
      disableNavigation
      || location.pathname === targetPath
      || isNavigatingRef.current
    ) {
      return;
    }

    isNavigatingRef.current = true;
    onClick?.();
    navigate(targetPath, {
      state: buildAlertDetailsLocationState(
        location.pathname,
        location.search,
        location.state,
      ),
    });

    window.setTimeout(() => {
      isNavigatingRef.current = false;
    }, 500);
  };

  const infoBoxOpts = {
    closeBoxURL: '',
    infoBoxClearance: new window.google.maps.Size(24, 24),
    pixelOffset: new window.google.maps.Size(-100, -20),
    alignBottom: true,
  };

  const infoString = alertEventsMap[name]?.shortDesc;
  const alertIcon = {
    url: getSvgByRisk(riskLevel as string),
    size: new google.maps.Size(22, 22),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(11, 11),
  };

  return (
    <MarkerF
      position={{ lat, lng }}
      icon={alertIcon}
      onClick={handleMarkerClick}
      onMouseOver={() => setShowInfoBox(true)}
      onMouseOut={() => setShowInfoBox(false)}
      zIndex={google.maps.Marker.MAX_ZINDEX + 100}
      options={{ optimized: false }}
    >
      {showInfoBox && infoString && (
        <InfoBoxF options={infoBoxOpts}>
          <S.InfoBox
            onMouseOver={() => setShowInfoBox(true)}
            onMouseLeave={() => setShowInfoBox(false)}
          >
            <div className="message">{infoString}</div>
          </S.InfoBox>
        </InfoBoxF>
      )}
    </MarkerF>
  );
}

export default AlertMarker;
